import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_THEME,
  THEMES,
  THEME_STORAGE_KEY,
  normalizeThemeId,
  type ThemeId,
  type ThemeLex,
} from "./themes";

/**
 * Theme runtime. The active theme drives two things:
 *   1. The PALETTE — via the `data-theme` attribute on <html>, which selects a
 *      block of CSS variables in globals.css. Pure CSS, no re-render needed.
 *   2. The LEXICON — the ThemeLex object, consumed by components via useLex()
 *      to render theme-specific labels.
 *
 * The initial value is read from the attribute the boot script (index.html)
 * already set before first paint, so there is no flash and no mismatch.
 */

interface ThemeContextValue {
  themeId: ThemeId;
  /** The active lexicon (labels + product name + background variant). */
  lex: ThemeLex;
  setTheme: (id: ThemeId) => void;
  toggleTheme: () => void;
}

function readInitialTheme(): ThemeId {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.dataset.theme;
    if (attr) return normalizeThemeId(attr);
  }
  if (typeof localStorage !== "undefined") {
    try {
      return normalizeThemeId(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
      /* ignore */
    }
  }
  return DEFAULT_THEME;
}

function applyTheme(id: ThemeId) {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = id;
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    /* storage blocked — palette still applied for this session */
  }
}

const ThemeContext = createContext<ThemeContextValue>({
  themeId: DEFAULT_THEME,
  lex: THEMES[DEFAULT_THEME],
  setTheme: () => undefined,
  toggleTheme: () => undefined,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeId] = useState<ThemeId>(readInitialTheme);

  const setTheme = useCallback((id: ThemeId) => {
    applyTheme(id);
    setThemeId(id);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeId((cur) => {
      const next: ThemeId = cur === "norse" ? "frontier" : "norse";
      applyTheme(next);
      return next;
    });
  }, []);

  useEffect(() => {
    document.title = `${THEMES[themeId].productName} // Command Surface`;
  }, [themeId]);

  const value = useMemo<ThemeContextValue>(
    () => ({ themeId, lex: THEMES[themeId], setTheme, toggleTheme }),
    [themeId, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Full theme controls (id, lexicon, setters). */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/** Shorthand for just the active lexicon. */
export function useLex(): ThemeLex {
  return useContext(ThemeContext).lex;
}
